import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  Alert, Box, Button, Checkbox, Chip, CircularProgress, FormControl, ListItemText, MenuItem,
  Paper, Select, TextField, Typography,
} from '@mui/material';
import { IoDownloadOutline, IoRefreshOutline } from 'react-icons/io5';
import * as XLSX from 'xlsx-js-style';
import AppDatatable from '../../components/AppDatatable';
import { exportClaimsPdf } from '../../utils/exportClaimsPdf';
import { getLoggedInUser } from '../../utils/permissions';
import { getDateRangeParams, getDefaultWeekRange } from '../../utils/dateRange';

const ENDPOINTS = [
  { url: '/get_claim_processing_forms', workflow: 'Vehicle Damage' },
  { url: '/get_shuttle_bus_claim_processing_forms', workflow: 'Shuttle Bus' },
  { url: '/get_windshield_tire_claim_processing_forms', workflow: 'Windshield / Tire' },
];

const HEADERS = [
  'Date of loss', 'R/A No.', 'Unit No.', 'Plate No.', 'VIN', 'Year & Make',
  'Model & Colour', 'Mileage', 'Insured', 'TP Insurance & Number', 'Pre Auth',
  'Pre Auth Taken', 'Damage', 'At Fault/ Not at Fault', 'Accident Narrative',
  'Claim Entry Date', "Renter's/Driver's Name", 'Demand', 'Maaco Estimate',
  'Blessing Estimate', 'Appraisal', 'Approved Estimate',
  'Charged/Received Customer', 'Handling Supervisor', 'Claim Status', 'Claim Closed Date',
];
const EXPORT_SECTIONS = [
  { workflow: 'Vehicle Damage', title: 'Vehicle Damage Claims' },
  { workflow: 'Windshield / Tire', title: 'Windshield/Tire Claims' },
  { workflow: 'Shuttle Bus', title: 'Shuttle Bus Claims' },
];

const firstValue = (...values) => values.find((value) => value !== undefined && value !== null && String(value).trim() !== '') ?? '';
const normalizeLocations = (value) => (Array.isArray(value) ? value : String(value || '').split(','))
  .map((item) => String(item).trim().toLowerCase())
  .filter(Boolean);
const firstVehicle = (row) => Array.isArray(row?.details_by_vehicle) ? (row.details_by_vehicle[0] || {}) : {};
const dateValue = (value) => {
  if (!value) return '';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date;
};
const displayDate = (value) => {
  const date = dateValue(value);
  return date instanceof Date ? date.toLocaleDateString('en-US', {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
  }) : String(date || '');
};
const plainText = (value) => String(value || '')
  .replace(/<br\s*\/?>/gi, '\n')
  .replace(/<\/p\s*>/gi, '\n')
  .replace(/<[^>]+>/g, '')
  .replace(/&nbsp;/gi, ' ')
  .replace(/&amp;/gi, '&')
  .replace(/&lt;/gi, '<')
  .replace(/&gt;/gi, '>')
  .replace(/&quot;/gi, '"')
  .replace(/&#39;/gi, "'")
  .replace(/\n{3,}/g, '\n\n')
  .trim();
const notesFor = (row) => {
  const entries = Array.isArray(row.internal_note_entries) ? row.internal_note_entries : [];
  const entryNotes = entries
    .map((entry) => plainText(firstValue(entry?.note, entry?.notes)))
    .filter(Boolean)
    .join(' | ');
  return firstValue(entryNotes, plainText(row.internal_notes));
};
const commentDateTime = (value) => {
  const date = dateValue(value);
  return date instanceof Date ? date.toLocaleString('en-US', {
    month: 'numeric', day: 'numeric', year: 'numeric',
    hour: 'numeric', minute: '2-digit',
  }) : String(date || '');
};
const internalCommentsFor = (row) => {
  const entries = Array.isArray(row.internal_note_entries) && row.internal_note_entries.length
    ? row.internal_note_entries
    : [{
      note: row.internal_notes,
      created_by: firstValue(row.updated_by, row.created_by),
      created_datetime: firstValue(row.updated_datetime, row.created_datetime),
    }];
  return entries.map((entry) => {
    const note = plainText(firstValue(entry?.note, entry?.notes));
    if (!note) return '';
    const author = firstValue(entry?.updated_by, entry?.created_by);
    const timestamp = commentDateTime(firstValue(entry?.updated_datetime, entry?.created_datetime));
    return [note, author, timestamp].filter(Boolean).join(' - ');
  }).filter(Boolean).join('\n\n');
};

const toReportRow = (row, workflow) => {
  const reported = row.reported_claim || {};
  const reportedVehicle = firstVehicle(reported);
  const vehicle = firstVehicle(row);
  const make = firstValue(vehicle.make, row.make);
  const modelColor = firstValue(
    row.model_colour,
    [vehicle.model, vehicle.color].filter(Boolean).join('*')
  );
  const fault = firstValue(row.party_at_fault, row.atFaultNotAtFault, row.fault_status);
  const demand = firstValue(row.original_demand_amount, row.demand_balance, row.amount_approved);
  const vehicleStatus = firstValue(
    row.vehicle_status,
    row.vehicleStatus,
    vehicle.vehicleStatus === 'Other' ? vehicle.vehicleStatusOther : vehicle.vehicleStatus,
    row.vehicle_location
  );

  return {
    id: `${workflow}-${row._id}`,
    workflow,
    location: firstValue(row.branchLocationCode, row.claimOwningBranch, row.location_name, 'Unassigned'),
    raNumber: firstValue(row.rentalAgreementNumber, row.ra_number),
    name: firstValue(row.authorizedDriverName, row.customerName, row.reportingEmployeeName),
    insurance: firstValue(row.customerInsuranceCarrier, row.insurance_route, row.insuranceType, row.insurance_number),
    unit: firstValue(vehicle.unitFleetNumber, row.unit),
    make: firstValue(make, row.year_make),
    modelColor,
    vin: firstValue(vehicle.vin, row.vin),
    damage: firstValue(row.reported_claim?.accidentDescription, row.accidentDescription),
    fault,
    dateOfLoss: firstValue(row.accidentDate, row.date_of_loss),
    notes: notesFor(row),
    claimEntryDate: firstValue(row.created_datetime, row.claim_entry_date),
    vehicleStatus,
    demand,
    phone: firstValue(row.adjuster_phone, row.customerPhone),
    repairedAmount: firstValue(row.repaired_amount),
    receivedAmount: row.mode_of_payment === 'Charged Renter' ? firstValue(row.payment_amount) : '',
    lou: firstValue(row.lou),
    adminFee: firstValue(row.admin_fee),
    towCheck: firstValue(row.tow_check),
    salvageAmount: firstValue(row.salvage_amount),
    sold: firstValue(row.sold),
    amountDueCustomer: firstValue(row.amount_due_customer),
    acv: firstValue(row.acv),
    chargeback: firstValue(row.chargeback),
    difference: firstValue(row.difference),
    chargedCustomer: firstValue(row.charged_customer),
    checkReceived: firstValue(row.check_received),
    wireTransfer: firstValue(row.wire_transfer),
    repairedAt: firstValue(row.repaired_at),
    plateNumber: firstValue(reportedVehicle.licensePlate, vehicle.licensePlate, reported.licensePlate, row.licensePlate, row.license_plate, row.plate_number),
    yearMake: firstValue(row.year_make, [vehicle.year, make].filter(Boolean).join(' ')),
    mileage: firstValue(reportedVehicle.odometerReading, vehicle.odometerReading, reported.odometerReading, row.odometerReading, row.mileage),
    insured: row.party_at_fault === 'Renter'
      && row.liability_accepted_by_insurance === "Renter's Ins"
      && row.renter_insurance_type === 'Personal'
      ? firstValue(row.renter_insurance_name)
      : firstValue(row.insured, row.insuredCompanyName, reported.insuredCompanyName),
    tpInsuranceNumber: [
      firstValue(row.liability_insurance_name),
      firstValue(row.insurance_claim_number),
    ].filter(Boolean).join(' - '),
    preAuth: firstValue(row.pre_auth, row.preAuthorizationAmount, reported.preAuthorizationAmount),
    preAuthTaken: firstValue(row.pre_auth_taken, row.preAuthorizationTaken, reported.preAuthorizationTaken),
    exportDamage: firstValue(reportedVehicle.vehicleDamageDescription, vehicle.vehicleDamageDescription, reported.vehicleDamageDescription, row.vehicleDamageDescription),
    accidentNarrative: internalCommentsFor(row),
    maacoEstimate: firstValue(row.maaco_estimate, row.maacoEstimate),
    blessingEstimate: firstValue(row.blessing_estimate, row.blessingEstimate),
    appraisal: firstValue(row.appraisal, row.appraisal_amount, row.pro_appraisal),
    approvedEstimate: firstValue(row.approved_estimate, row.amount_approved),
    handlingSupervisor: firstValue(row.updated_by),
    claimStatus: firstValue(row.claim_status === 'Other' ? row.claim_status_other : row.claim_status, row.claim_status),
    closedDate: firstValue(row.closed_datetime, row.closure_datetime),
  };
};

const EXCEL_KEYS = [
  'dateOfLoss', 'raNumber', 'unit', 'plateNumber', 'vin', 'yearMake',
  'modelColor', 'mileage', 'insured', 'tpInsuranceNumber', 'preAuth',
  'preAuthTaken', 'exportDamage', 'fault', 'accidentNarrative', 'claimEntryDate',
  'name', 'demand', 'maacoEstimate', 'blessingEstimate', 'appraisal',
  'approvedEstimate', 'receivedAmount', 'handlingSupervisor', 'claimStatus', 'closedDate',
];
const EXCEL_DATE_KEYS = new Set(['dateOfLoss', 'claimEntryDate', 'closedDate']);
const EXCEL_MONEY_KEYS = new Set([
  'preAuth', 'demand', 'maacoEstimate', 'blessingEstimate', 'appraisal',
  'approvedEstimate', 'receivedAmount',
]);
const excelValues = (row) => EXCEL_KEYS.map((key) => {
  const value = row[key];
  if (EXCEL_DATE_KEYS.has(key)) return dateValue(value);
  if (EXCEL_MONEY_KEYS.has(key) && value !== '') {
    const number = Number(value);
    return Number.isFinite(number) ? number : value;
  }
  return value;
});

const safeSheetName = (name, used) => {
  const base = String(name || 'Unassigned').replace(/[\\/?*[\]:]/g, ' ').trim().slice(0, 23) || 'Unassigned';
  let candidate = `${base} Damages`.slice(0, 31);
  let index = 2;
  while (used.has(candidate)) candidate = `${base.slice(0, 26)} ${index++}`.slice(0, 31);
  used.add(candidate);
  return candidate;
};

export default function PendingClaimsReport() {
  const baseURL = process.env.REACT_APP_API_BASE_URL;
  const currentUser = useMemo(() => getLoggedInUser(), []);
  const assignedLocations = useMemo(
    () => normalizeLocations(currentUser?.selected_locations || currentUser?.locations),
    [currentUser]
  );
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exportingPdf, setExportingPdf] = useState(false);
  const [error, setError] = useState('');
  const [selectedLocations, setSelectedLocations] = useState([]);
  const [workflow, setWorkflow] = useState('All');
  const [search, setSearch] = useState('');
  const [fromDate, setFromDate] = useState(() => getDefaultWeekRange().fromDate);
  const [toDate, setToDate] = useState(() => getDefaultWeekRange().toDate);

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const responses = await Promise.all(ENDPOINTS.map((endpoint) => axios.get(`${baseURL}${endpoint.url}`, { params: getDateRangeParams(fromDate, toDate) })));
      const result = responses.flatMap((response, index) => {
        const payload = response.data;
        const records = Array.isArray(payload) ? payload : (payload?.data || payload?.records || []);
        return records.map((record) => toReportRow(record, ENDPOINTS[index].workflow));
      });
      setRows(result);
    } catch (requestError) {
      setError(requestError?.response?.data?.message || 'Pending claims could not be loaded.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [baseURL, fromDate, toDate]); // eslint-disable-line react-hooks/exhaustive-deps

  const scopedRows = useMemo(() => {
    if (!assignedLocations.length) return [];
    return rows.filter((row) => assignedLocations.includes(String(row.location || '').trim().toLowerCase()));
  }, [assignedLocations, rows]);
  const locations = useMemo(() => [...new Set(scopedRows.map((row) => row.location))].sort(), [scopedRows]);
  const filteredRows = useMemo(() => {
    const query = search.trim().toLowerCase();
    return scopedRows.filter((row) => {
      const lossDate = row.dateOfLoss ? new Date(row.dateOfLoss) : null;
      const validLossDate = lossDate && !Number.isNaN(lossDate.getTime());
      const from = fromDate ? new Date(`${fromDate}T00:00:00`) : null;
      const to = toDate ? new Date(`${toDate}T23:59:59.999`) : null;
      return (
        (!selectedLocations.length || selectedLocations.includes(row.location)) &&
        (workflow === 'All' || row.workflow === workflow) &&
        (!from || (validLossDate && lossDate >= from)) &&
        (!to || (validLossDate && lossDate <= to)) &&
        (!query || Object.values(row).some((value) => String(value ?? '').toLowerCase().includes(query)))
      );
    });
  }, [scopedRows, selectedLocations, workflow, search, fromDate, toDate]);

  const exportPdf = async () => {
    setExportingPdf(true);
    setError('');
    try {
      await exportClaimsPdf({
        title: 'Pending Claims Report', headers: HEADERS, keys: EXCEL_KEYS,
        moneyKeys: EXCEL_MONEY_KEYS, rows: filteredRows, sections: EXPORT_SECTIONS,
        valuesFor: excelValues,
        filename: `Pending_Claims_Report_${new Date().toISOString().slice(0, 10)}.pdf`,
      });
    } catch (exportError) {
      setError('PDF export failed. Please try again.');
    } finally {
      setExportingPdf(false);
    }
  };

  const exportReport = () => {
    const workbook = XLSX.utils.book_new();
    const grouped = filteredRows.reduce((result, row) => {
      (result[row.location] ||= []).push(row);
      return result;
    }, {});
    const usedNames = new Set();
    Object.entries(grouped).sort(([a], [b]) => a.localeCompare(b)).forEach(([branch, branchRows]) => {
      const sheetRows = [];
      const sectionRanges = [];
      const headerRanges = [];
      EXPORT_SECTIONS.forEach((section, sectionIndex) => {
        const sectionRows = branchRows
          .filter((row) => row.workflow === section.workflow)
          .sort((a, b) => {
            const aDate = new Date(a.dateOfLoss).getTime();
            const bDate = new Date(b.dateOfLoss).getTime();
            const aSortValue = Number.isNaN(aDate) ? Number.POSITIVE_INFINITY : aDate;
            const bSortValue = Number.isNaN(bDate) ? Number.POSITIVE_INFINITY : bDate;
            return aSortValue - bSortValue;
          });
        const titleRow = sheetRows.length;
        sheetRows.push([section.title]);
        sectionRanges.push(titleRow);
        headerRanges.push(sheetRows.length);
        sheetRows.push(HEADERS);
        if (sectionRows.length) {
          sheetRows.push(...sectionRows.map(excelValues));
        } else {
          sheetRows.push(['No pending claims for this section']);
        }
        if (sectionIndex < EXPORT_SECTIONS.length - 1) sheetRows.push([]);
      });
      const sheet = XLSX.utils.aoa_to_sheet(sheetRows, { dateNF: 'mm/dd/yyyy' });
      sheet['!merges'] = [];
      sectionRanges.forEach((rowIndex) => {
        sheet['!merges'].push({ s: { r: rowIndex, c: 0 }, e: { r: rowIndex, c: HEADERS.length - 1 } });
      });
      sectionRanges.forEach((rowIndex) => {
        for (let column = 0; column < HEADERS.length; column += 1) {
          const address = XLSX.utils.encode_cell({ r: rowIndex, c: column });
          if (!sheet[address]) sheet[address] = { t: 's', v: '' };
          sheet[address].s = {
            font: { bold: true, color: { rgb: 'FFFFFF' }, sz: 13 },
            fill: { patternType: 'solid', fgColor: { rgb: '173B70' } },
            alignment: { vertical: 'center' },
          };
        }
      });
      headerRanges.forEach((rowIndex) => {
        for (let column = 0; column < HEADERS.length; column += 1) {
          const address = XLSX.utils.encode_cell({ r: rowIndex, c: column });
          if (!sheet[address]) continue;
          sheet[address].s = {
            font: { bold: true, color: { rgb: '173B70' } },
            fill: { patternType: 'solid', fgColor: { rgb: 'DCE8F7' } },
            alignment: { vertical: 'center', wrapText: false },
          };
        }
      });
      sheet['!rows'] = sheetRows.map((_, rowIndex) => ({
        hpt: sectionRanges.includes(rowIndex) ? 24 : 18,
      }));
      sheet['!cols'] = [
        16, 16, 14, 16, 22, 20, 24, 14, 24, 30, 16, 18, 40,
        26, 50, 18, 28, 18, 20, 22, 18, 22, 28, 24, 22, 20,
      ].map((wch) => ({ wch }));
      Object.keys(sheet).forEach((address) => {
        if (address.startsWith('!')) return;
        const cell = sheet[address];
        const key = EXCEL_KEYS[XLSX.utils.decode_cell(address).c];
        cell.s = { ...cell.s, alignment: { ...cell.s?.alignment, wrapText: false } };
        if (EXCEL_MONEY_KEYS.has(key) && cell?.t === 'n') cell.z = '$#,##0.00';
        if (cell?.v instanceof Date) cell.z = 'mm/dd/yyyy';
      });
      XLSX.utils.book_append_sheet(workbook, sheet, safeSheetName(branch, usedNames));
    });
    if (!filteredRows.length) {
      XLSX.utils.book_append_sheet(workbook, XLSX.utils.aoa_to_sheet([HEADERS]), 'Pending Damages');
    }
    XLSX.writeFile(
      workbook,
      `Pending_Claims_Report_${new Date().toISOString().slice(0, 10)}.xlsx`,
      { cellStyles: true }
    );
  };

  const columns = [
    { field: 'raNumber', label: 'R/A', sortable: true },
    { field: 'name', label: 'Renters Name', sortable: true },
    { field: 'insurance', label: 'Insurance', sortable: true },
    { field: 'unit', label: 'Unit', sortable: true },
    { field: 'make', label: 'Make', sortable: true },
    { field: 'modelColor', label: 'Model & Color', sortable: true },
    { field: 'vin', label: 'Vin Number', sortable: true },
    { field: 'damage', label: 'Damage', sortable: true },
    { field: 'fault', label: 'At Fault/ Not at Fault', sortable: true },
    { field: 'dateOfLossDisplay', label: 'Date of Loss', sortable: true, getSortValue: (row) => row.dateOfLoss },
    { field: 'notes', label: 'Notes', sortable: true },
  ];
  const tableRows = filteredRows.map((row) => ({
    ...row,
    dateOfLossDisplay: displayDate(row.dateOfLoss),
  }));

  return (
    <Box sx={{ ml: { md: '240px' }, mt: '64px', p: { xs: 1.5, sm: 2.5, lg: 4 }, minHeight: 'calc(100vh - 64px)', bgcolor: '#f5f7fb', overflow: 'hidden' }}>
      <Paper sx={{ p: { xs: 2, sm: 3, lg: 4 }, borderRadius: { xs: 2, md: 4 }, boxShadow: '0 12px 35px rgba(23,57,113,.07)', overflow: 'hidden' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%', gap: 2.5, mb: 3 }}>
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontSize: { xs: 28, md: 36 }, lineHeight: 1.15, fontWeight: 800, color: '#123b76' }}>Pending Claims Report</Typography>
            <Typography sx={{ mt: 0.75, color: 'text.secondary', fontSize: { xs: 14, md: 16 } }}>Review pending claims and export the pending workbook.</Typography>
          </Box>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', sm: 'repeat(4, minmax(0, 1fr))' }, alignItems: 'center', gap: 1.5, alignSelf: 'flex-end', width: { xs: '100%', sm: 'auto' }, '& > .MuiButton-root, & > .MuiChip-root': { width: '100%', height: 44, minWidth: { xs: 0, sm: 140 }, whiteSpace: 'nowrap' }, '& > .MuiButton-root': { px: { xs: 1, sm: 2 }, fontSize: { xs: 12, sm: 14 } } }}>
            <Chip sx={{ height: 44, px: 0.75, bgcolor: '#f5f9ff', '& .MuiChip-label': { px: 1.25 } }} label={`${filteredRows.length} pending claim${filteredRows.length === 1 ? '' : 's'}`} color="primary" variant="outlined" />
            <Button sx={{ height: 44, minWidth: 118, borderRadius: 2, px: 2 }} variant="outlined" startIcon={<IoRefreshOutline />} onClick={load} disabled={loading}>Refresh</Button>
            <Button sx={{ height: 44, minWidth: 154, borderRadius: 2, px: 2.25 }} variant="contained" startIcon={<IoDownloadOutline />} onClick={exportReport} disabled={loading}>Export Excel</Button>
            <Button sx={{ height: 44, minWidth: 154, borderRadius: 2 }} variant="outlined" startIcon={<IoDownloadOutline />} onClick={exportPdf} disabled={loading || exportingPdf}>{exportingPdf ? 'Exporting PDF...' : 'Export PDF'}</Button>
          </Box>
        </Box>

        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', xl: '1fr 1fr 1fr 1fr' }, gap: 2, mb: 3, p: { xs: 2, md: 2.5 }, borderRadius: 2.5, bgcolor: '#f7f9fc', border: '1px solid #e7edf5' }}>
          <Box>
            <Typography component="label" sx={{ display: 'block', mb: 0.75, ml: 0.25, fontSize: 13, fontWeight: 600, color: '#36577f' }}>Location</Typography>
            <FormControl size="small" fullWidth>
              <Select multiple value={selectedLocations} onChange={(event) => setSelectedLocations(typeof event.target.value === 'string' ? event.target.value.split(',') : event.target.value)} displayEmpty renderValue={(selected) => selected.length ? selected.join(', ') : 'All assigned locations'} inputProps={{ 'aria-label': 'Location' }}>
                {locations.map((item) => <MenuItem key={item} value={item}><Checkbox checked={selectedLocations.includes(item)} /><ListItemText primary={item} /></MenuItem>)}
              </Select>
            </FormControl>
          </Box>
          <Box>
            <Typography component="label" sx={{ display: 'block', mb: 0.75, ml: 0.25, fontSize: 13, fontWeight: 600, color: '#36577f' }}>Claim type</Typography>
            <FormControl size="small" fullWidth>
              <Select value={workflow} onChange={(event) => setWorkflow(event.target.value)} inputProps={{ 'aria-label': 'Claim type' }}>
                <MenuItem value="All">All claim types</MenuItem>
                {ENDPOINTS.map((item) => <MenuItem key={item.workflow} value={item.workflow}>{item.workflow}</MenuItem>)}
              </Select>
            </FormControl>
          </Box>
          <Box>
            <Typography component="label" htmlFor="pending-loss-date-from" sx={{ display: 'block', mb: 0.75, ml: 0.25, fontSize: 13, fontWeight: 600, color: '#36577f' }}>
              Loss date from
            </Typography>
            <TextField
              id="pending-loss-date-from"
              type="date"
              value={fromDate}
              onChange={(event) => setFromDate(event.target.value)}
              size="small"
              fullWidth
              inputProps={{ max: toDate || undefined, 'aria-label': 'Loss date from' }}
            />
          </Box>
          <Box>
            <Typography component="label" htmlFor="pending-loss-date-to" sx={{ display: 'block', mb: 0.75, ml: 0.25, fontSize: 13, fontWeight: 600, color: '#36577f' }}>
              Loss date to
            </Typography>
            <TextField
              id="pending-loss-date-to"
              type="date"
              value={toDate}
              onChange={(event) => setToDate(event.target.value)}
              size="small"
              fullWidth
              inputProps={{ min: fromDate || undefined, 'aria-label': 'Loss date to' }}
            />
          </Box>
        </Box>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {loading
          ? <Box py={8} textAlign="center"><CircularProgress /></Box>
          : <Box sx={{
              '& > .MuiPaper-root': { p: 0, boxShadow: 'none', borderRadius: 0 },
              '& .MuiTableContainer-root': { border: '1px solid #e8edf4', borderRadius: 2, maxWidth: '100%' },
              '& .MuiTableHead-root': { bgcolor: '#f6f8fc' },
              '& .MuiTableCell-head': { color: '#173b70', py: 1.75 },
              '& .MuiTableCell-body': { py: 1.5, color: '#294d7e' },
              '& .MuiTableRow-root:hover': { bgcolor: '#f8fbff' },
            }}>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                <TextField
                  label="Search table"
                  placeholder="R/A, customer, insurance or unit"
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  size="small"
                  sx={{ width: { xs: '100%', sm: 360 } }}
                />
              </Box>
              <AppDatatable
                data={{ rows: tableRows, columns }}
                hideSearch
                tableMinWidth={1800}
                noFoundMessage="No pending claims match the selected filters."
              />
            </Box>
        }
      </Paper>
    </Box>
  );
}
